import { useEffect, useState } from 'react'
import { Caption, COLORS, Panel, Badge } from './ui'

// NEW chapter — how modern vision encoders are actually trained: self-supervised,
// on unlabeled data, producing a general "foundation" backbone (DINOv2-style).
const HEADS = [
  { label: 'Classification', color: '#818cf8' },
  { label: 'Detection', color: '#a855f7' },
  { label: 'Segmentation', color: '#ec4899' },
  { label: 'Depth', color: '#f97316' },
]

export function Foundation2D() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768)
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < 768)
    window.addEventListener('resize', handler)
    return () => window.removeEventListener('resize', handler)
  }, [])
  const arrow = <span style={{ color: COLORS.indigo, fontSize: 22, opacity: 0.6, transform: mobile ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>→</span>

  return (
    <div style={{
      position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflowY: 'auto',
      alignItems: 'center', justifyContent: 'center', gap: 16, padding: mobile ? '80px 20px 90px' : '90px 24px 110px',
      fontFamily: "'General Sans', system-ui, sans-serif", textAlign: 'center', color: COLORS.text,
    }}>
      <Badge color={COLORS.green}>Foundation & Self-Supervised Learning</Badge>

      <div style={{ display: 'flex', flexDirection: mobile ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: mobile ? 14 : 18, flexWrap: 'wrap', width: mobile ? '100%' : undefined }}>
        {/* Unlabeled data */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: mobile ? '100%' : undefined }}>
          <div style={{ position: 'relative', width: 110, height: 96 }}>
            {[0, 1, 2].map(k => (
              <img key={k} src="/cv/ref-cat.png" alt="" style={{
                position: 'absolute', left: k * 10, top: k * 8, width: 86, height: 72,
                objectFit: 'cover', borderRadius: 8, border: '1px solid rgba(255,255,255,0.15)', filter: `hue-rotate(${k * 40}deg)`,
              }} />
            ))}
          </div>
          <div style={{ fontSize: 13, color: COLORS.textDim, maxWidth: 130 }}>millions of <strong>unlabeled</strong> images</div>
        </div>

        {arrow}

        {/* Pretext task */}
        <Panel glow={COLORS.green} style={{ width: mobile ? '100%' : 180 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.green }}>Self-supervised pretext</div>
          <div style={{ fontSize: 12.5, color: COLORS.textDim, marginTop: 6, lineHeight: 1.5 }}>
            mask patches & predict them · match two augmented views — <em>no labels</em>
          </div>
        </Panel>

        {arrow}

        {/* Frozen backbone → heads */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, width: mobile ? '100%' : undefined }}>
          <Panel glow={COLORS.indigoLight} style={{ padding: '10px 16px', width: mobile ? '100%' : undefined }}>
            <div style={{ fontSize: 14.5, fontWeight: 800 }}>🧊 Frozen backbone</div>
            <div style={{ fontSize: 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace", color: COLORS.textFaint }}>DINOv2 · general features</div>
          </Panel>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center', maxWidth: 230 }}>
            {HEADS.map(h => (
              <span key={h.label} style={{ fontSize: 12.5, padding: '4px 9px', borderRadius: 6, color: h.color, background: `${h.color}1c`, border: `1px solid ${h.color}55` }}>
                {h.label}
              </span>
            ))}
          </div>
        </div>
      </div>

      <Caption>
        Labelling images is expensive — so modern encoders learn from <strong>unlabeled</strong> data by solving
        invented puzzles (mask-and-predict, view-matching). The result is a reusable <em>foundation</em> model like
        DINOv2: train once, then attach lightweight heads for many tasks.
      </Caption>
    </div>
  )
}
